import React, { useCallback, useState } from "react";
import { Col, Form, Modal, Space, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import { linkTSRToCustomers } from "utils/api";
import CustomButton from "components/Common/buttons/CustomButton";
import uploadArea from "assets/images/svg/uploadArea.svg";
import Dragger from "antd/lib/upload/Dragger";
import * as xlsx from "xlsx";
import { defaultNgiErrorMethod, errorMessage, successMessage } from "components/Common/responses/message";
import NGIForm from "components/Common/form/NGIForm";
import NGIFormItem from "components/Common/form/NGIFormItem";
import { useSelector } from "react-redux";
import { TSRSelector } from "store/TSR/selectors";
import closeIcon from "assets/images/svg/closeSmall.svg";
import { makeStyles } from "@material-ui/core/styles";
import { AUTH_USER, CSV_TEMPLATES, USER_ROLES } from "utils/constants";
import TSRSelect from "components/Common/components/TSRSelect";
import CustomLink from "components/Common/buttons/CustomLink";
import { generateCSV } from "utils/helpers/functions";

const useStyles = makeStyles({
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: "5px 10px",
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
});

const BulkLinkTSRModal = ({ onClose, onUpdate }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [customerIds, setCustomerIds] = useState([]);
  const [isLoading, setLoader] = useState(false);
  const { TSRList } = useSelector(TSRSelector);
  const [file, setFile] = useState(null);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  const readUploadFile = useCallback(e => {
    const file = e.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        const data = e.target.result;
        const workbook = xlsx.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet, { raw: false });
        const formattedResult = json.map(item => {
          if (item.customerId) return item.customerId ? item.customerId : null;
          return null;
        });
        const noError = formattedResult.every(item => item || null);
        !noError && errorMessage("Please upload the correct ‘*.csv’ or ‘*.xlsx’ file with customerId fields");

        if (noError) {
          setFile(file);
          setCustomerIds(formattedResult);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const handleSave = useCallback(() => {
    const { technician, growthTechnician } = form.getFieldsValue();
    if (technician && growthTechnician && customerIds.length) {
      setLoader(true);
      const selectedTechnician = TSRList.find(item => item.id === technician);
      const selectedGrowthTechnician = TSRList.find(item => item.id === growthTechnician);
      linkTSRToCustomers({
        customerIds,
        technician: selectedTechnician.email,
        technicianName: selectedTechnician.name,
        growthTechnician: selectedGrowthTechnician.email,
        growthTechnicianName: selectedGrowthTechnician.name,
      })
        .then(() => {
          setTimeout(() => {
            successMessage("Customers were linked with the new TSR successfully");
            setLoader(false);
            onUpdate();
            onClose();
          }, 2000);
        })
        .catch(err => {
          defaultNgiErrorMethod(err, 0);
          setLoader(false);
        });
    }
  }, [form, customerIds, TSRList]);

  const removeFile = useCallback(() => {
    setFile(null);
    setCustomerIds([]);
  }, []);

  return (
    <Modal open={true} title="Bulk link customers to TSR" closable={false} onCancel={handleCancel} footer={[]}>
      <Spin spinning={isLoading}>
        <Space>
          <CustomLink color="#0085FF" onClick={() => generateCSV(CSV_TEMPLATES.CUSTOMER_BULK_LINK_TSR)}>
            Download *.csv template
          </CustomLink>
        </Space>
        <NGIForm name="bulkLinkCustomersToTSR" form={form} onFinish={handleSave}>
          <Col span={24}>
            {file ? (
              <div className={classes.fileContainer}>
                <div className={classes.fileTitle}>{`${file.name} (${customerIds.length})`}</div>
                <div onClick={removeFile} className={classes.removeBtn}>
                  <img src={closeIcon} alt="remove" />
                </div>
              </div>
            ) : (
              <Dragger showUploadList={false} customRequest={readUploadFile} name="file">
                <img className="upload-bg" src={uploadArea} alt="upload" />
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">
                  Support for a single upload. Strictly prohibit from uploading company data or other band files
                </p>
              </Dragger>
            )}
          </Col>
          <br />
          <Col span={24}>
            <Title level={5}>Growth TSR</Title>
            <NGIFormItem name="growthTechnician" rules={[{ required: true, message: "Please select Growth TSR" }]}>
              <TSRSelect
                showSearch
                filterBy={{
                  field: "team",
                  value: "Growth",
                }}
                initialList={TSRList.filter(item => item.depot === AUTH_USER()?.depotName && item.team === "Growth")}
                roles={[USER_ROLES.MMT, USER_ROLES.TECHNICIANS, USER_ROLES.MTSR, USER_ROLES.MDSC]}
                depot={AUTH_USER()?.depotName}
              />
            </NGIFormItem>
          </Col>
          <Col span={24}>
            <Title level={5}>Maintenance TSR</Title>
            <NGIFormItem name="technician" rules={[{ required: true, message: "Please select Maintenance TSR" }]}>
              <TSRSelect
                showSearch
                filterBy={{
                  field: "team",
                  value: "Maintenance",
                }}
                initialList={TSRList.filter(
                  item => item.depot === AUTH_USER()?.depotName && item.team === "Maintenance"
                )}
                roles={[USER_ROLES.MMT, USER_ROLES.TECHNICIANS, USER_ROLES.MTSR, USER_ROLES.MDSC]}
                depot={AUTH_USER()?.depotName}
              />
            </NGIFormItem>
          </Col>
          <Space>
            <CustomButton onClick={handleCancel} type="primary" color="outlined" size="small" text="Cancel" />
            <CustomButton htmlType="submit" type="primary" size="small" text="Save" />
          </Space>
        </NGIForm>
      </Spin>
    </Modal>
  );
};

export default BulkLinkTSRModal;
